import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";

export const Route = createFileRoute("/legal/modern-slavery")({
  head: () => ({
    meta: [
      { title: "Modern Slavery Statement — The Hospitality Reserve" },
      {
        name: "description",
        content:
          "Our voluntary commitment to combating modern slavery, human trafficking, and exploitation in the UK hospitality supply chain.",
      },
      { property: "og:title", content: "Modern Slavery Statement — The Hospitality Reserve" },
      { property: "og:url", content: "https://thehospitalityreserve.com/legal/modern-slavery" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/legal/modern-slavery" }],
  }),
  component: ModernSlaveryPage,
});

function ModernSlaveryPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Modern Slavery & Safeguarding Statement"
      updated="21 June 2026"
    >
      <p>
        This statement is made on a voluntary basis pursuant to the principles
        of section 54 of the Modern Slavery Act 2015. The Hospitality Reserve
        ("the Platform") is committed to acting ethically and with integrity
        in all of our operations and relationships, and to implementing and
        enforcing effective systems and controls to ensure modern slavery is
        not taking place anywhere in our own operations or in those of any
        venue or contractor who uses the Platform.
      </p>

      <h2>About us</h2>
      <p>
        The Hospitality Reserve is a neutral technology platform that connects
        UK hospitality venues with vetted, self-employed bar professionals. We
        are not an employer, employment business, or employment agency.
      </p>

      <h2>Our policies</h2>
      <ul>
        <li>
          <strong>Right to work.</strong> Every bartender on the Platform must
          provide documentary evidence of their right to work in the UK
          before accepting paid engagements. Documents are reviewed by an
          administrator and an audit trail is retained for six years.
        </li>
        <li>
          <strong>Age verification.</strong> Bartenders must be 18 or over to
          serve alcohol in the UK. Date of birth is captured during onboarding
          and an immutable audit log records each verification.
        </li>
        <li>
          <strong>Identity verification.</strong> Government ID and a live
          selfie check are required via Stripe Identity before applications
          are unlocked.
        </li>
        <li>
          <strong>Direct payment.</strong> Wages flow directly from the venue
          (via the Platform's regulated payments partner) to the contractor's
          own bank account through Stripe Connect. There are no third-party
          intermediaries holding contractor pay.
        </li>
        <li>
          <strong>Fair pay.</strong> Pay rates are set by role and are not
          negotiable; contractors keep 100% of the published hourly wage and
          100% of any tips allocated by the venue.
        </li>
        <li>
          <strong>Reporting channels.</strong> Any user may raise a safety,
          welfare or exploitation concern in-app via the incident reporting
          flow. Concerns are reviewed by the admin team and, where
          appropriate, escalated to law enforcement and to the Gangmasters
          and Labour Abuse Authority (GLAA).
        </li>
      </ul>

      <h2>Risk and due diligence</h2>
      <p>
        We consider the highest-risk areas in our model to be (a) cash-based
        off-Platform engagements and (b) third parties impersonating bona
        fide contractors. Our controls include identity verification, right
        to work review, anti-circumvention clauses in the Contractor
        Agreement and Terms, suspension powers for accounts that show signs
        of coercion or document tampering, and continuous monitoring of
        chat content for indicators of exploitation.
      </p>

      <h2>Training</h2>
      <p>
        Admin reviewers complete safeguarding and modern-slavery awareness
        training before being granted approval rights, and refresher
        materials are issued annually.
      </p>

      <h2>Reporting concerns</h2>
      <p>
        If you suspect modern slavery, trafficking, or labour exploitation in
        connection with the Platform, please contact the Modern Slavery
        Helpline on 08000 121 700 (24/7, free, confidential), or email
        <a href="mailto:safeguarding@thehospitalityreserve.com"> safeguarding@thehospitalityreserve.com</a>.
      </p>
    </LegalLayout>
  );
}
